import React, { useState, useEffect } from 'react';
import {
    Box,
    Flex,
    Text,
    IconButton as ChakraIconButton,
    Button,
    HStack,
    VStack,
    Link as ChakraLink,
    useDisclosure,
    Container,
    Image,
    Drawer,
    DrawerBody,
    DrawerHeader,
    DrawerOverlay,
    DrawerContent,
    DrawerCloseButton,
    useBreakpointValue,
    Icon,
    useToast
} from '@chakra-ui/react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import { SiX } from 'react-icons/si';
import { FaDiscord, FaMedium, FaBars, FaCoins } from 'react-icons/fa';
import { useBalance } from '../context/BalanceContext';
import { useWallet, useConnection } from '@solana/wallet-adapter-react';
import { useWalletModal } from '@solana/wallet-adapter-react-ui';
import SocialPrompt from './SocialPrompt';
import WhitelistAccessDenied from './WhitelistAccessDenied';

const MotionBox = styled(Box)`
  background: transparent !important;
  transition: all 0.3s ease !important;
  border: 2px solid #00e676 !important;
  border-radius: 25px !important;
  padding: 0 24px !important;
  height: 40px !important;
  color: #00e676 !important;
  font-weight: 600 !important;
  
  &:hover {
    background: #00e676 !important;
    color: black !important;
  }
  
  &:not([disabled]):hover {
    background: #00e676 !important;
    color: black !important;
  }
`;

const StyledWalletButton = styled(Button)`
  background: transparent !important;
  transition: all 0.3s ease !important;
  border: 2px solid #00e676 !important;
  border-radius: 25px !important;
  padding: 0 24px !important;
  height: 40px !important;
  color: #00e676 !important;
  font-weight: 600 !important;
  
  &:hover {
    background: #00e676 !important;
    color: black !important;
  }
  
  &:not([disabled]):hover {
    background: #00e676 !important;
    color: black !important;
  }
`;

const NavBar = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const isMobile = useBreakpointValue({ base: true, md: false });
    const toast = useToast();
    const { setVisible } = useWalletModal();
    const { connected, connecting } = useWallet();
    const { connection } = useConnection();
    const [showSocialPrompt, setShowSocialPrompt] = useState(false);
    const [showAccessDeniedPrompt, setShowAccessDeniedPrompt] = useState(false);

    // Function to handle social prompt completion
    const handleSocialPromptComplete = () => {
        setShowSocialPrompt(false);
        setShowAccessDeniedPrompt(true);
    };

    // Function to handle coming soon prompt completion
    const handleComingSoonComplete = () => {
        navigate('/modular-profile');
    };

    // Use the balance context
    const balanceContext = useBalance();
    const balance = balanceContext?.balance || 0;
    
    // Check if we're on the game page
    const isGamePage = location.pathname === '/game';

    // Add state to track if we need to show the wallet required message
    const [walletRequired, setWalletRequired] = useState(false);

    // Check wallet connection status
    useEffect(() => {
        // Only navigate if the wallet is connected AND was previously required
        if (connected && walletRequired) {
            // If wallet is now connected and was previously required, navigate to game
            setTimeout(() => {
                navigate('/game');
                setWalletRequired(false);
                
                // Skip wallet connected toast to prevent duplicate messages
                console.log("Wallet connected in NavBar");
            }, 500); // Small delay to ensure wallet is fully connected
        }
    }, [connected, walletRequired, navigate, toast]);

    const navItems = [
        { label: 'Home', path: '/' },
        { label: 'Buy Now', path: '/auction', isSpecial: true },
        { label: 'Tokenomics', path: '#tokenomics' },
        { label: 'Roadmap', path: '#roadmap' },
        { label: 'Whitepaper', path: '/whitepaper' },
        { label: 'DAO', path: '/dao' },
    ];

    const handleNavClick = (e, path) => {
        e.preventDefault();
        console.log('Nav click:', path, { connected, connecting });
        
        // Handle full page routes
        if (path.startsWith('/')) {
            // Simply redirect to the auction page which is protected by WhitelistGuard
            if (path === '/auction') {
                console.log('Redirecting to auction page with WhitelistGuard');
                navigate(path);
                window.scrollTo(0, 0);
                if (isMobile) onClose();
                return;
            }
            
            navigate(path);
            window.scrollTo(0, 0);
            if (isMobile) onClose();
            return;
        }

        // Handle hash navigation
        if (path.startsWith('#')) {
            const targetId = path.slice(1);
            
            if (location.pathname !== '/') {
                // Navigate to home and scroll to section
                navigate('/', {
                    state: { scrollTo: targetId }
                });
            } else {
                // If already on homepage, just scroll
                const element = document.getElementById(targetId);
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth' });
                }
            }
            if (isMobile) onClose();
        }
    };

    const handleEnterRealm = () => {
        // Always show social prompt first to enforce gating
        setShowSocialPrompt(true);
        
        // If not connected, show a toast suggesting to connect wallet
        if (!connected) {
            toast({
                title: "Wallet Connection Recommended",
                description: "You can also use a password to access Palm Heist",
                status: "info",
                duration: 5000,
                isClosable: true,
            });
        }
    };

    return (
        <Box
            position="fixed"
            top={0}
            left={0}
            right={0}
            zIndex={1000}
            as="nav"
        >
            <Box
                bg="#0D1019"
                width="100%"
                transition="all 0.3s ease"
                boxShadow="0px 4px 20px rgba(0, 0, 0, 0.4)"
            >
                <Container maxW="100%" px={4}>
                    <Flex h={16} alignItems="center" justify="space-between">
                        {/* Left side - Logo and Title */}
                        <Box>
                            <Link to="/">
                                <Flex align="center">
                                    <Image
                                        src="/images/palm-heist-logo.png"
                                        alt="PALMHEIST Logo"
                                        h="48px"
                                        objectFit="contain"
                                        mr={3}
                                    />
                                    <Text
                                        fontSize="2xl"
                                        fontWeight="bold"
                                        color="white"
                                        bgGradient="linear(to-r, #00E676, #00B894)"
                                        bgClip="text"
                                    >
                                        PALMHEIST
                                    </Text>
                                </Flex>
                            </Link>
                        </Box>

                        {/* Center - Navigation Links */}
                        {!isMobile && (
                            <Flex flex={1} justify="center" mx={8}>
                                <HStack spacing={8}>
                                    {navItems.map((link) => (
                                        <Box key={link.path} position="relative">
                                            <Link
                                                to={link.path}
                                                onClick={(e) => handleNavClick(e, link.path)}
                                            >
                                                <Text
                                                    color={link.isSpecial ? "#00e676" : "white"}
                                                    _hover={{
                                                        color: "#00e676",
                                                    }}
                                                    px={4}
                                                    fontWeight="medium"
                                                    transition="color 0.2s"
                                                >
                                                    {link.label}
                                                </Text>
                                                {link.isSpecial && (
                                                    <Box
                                                        position="absolute"
                                                        top="2px"
                                                        right="6px"
                                                        width="6px"
                                                        height="6px"
                                                        borderRadius="full"
                                                        bg="red.500"
                                                        sx={{
                                                            '@keyframes pulse': {
                                                                '0%': {
                                                                    transform: 'scale(0.95)',
                                                                    opacity: 0.5,
                                                                },
                                                                '50%': {
                                                                    transform: 'scale(1.1)',
                                                                    opacity: 0.8,
                                                                },
                                                                '100%': {
                                                                    transform: 'scale(0.95)',
                                                                    opacity: 0.5,
                                                                },
                                                            },
                                                            animation: 'pulse 1.5s infinite',
                                                        }}
                                                        _after={{
                                                            content: '""',
                                                            position: "absolute",
                                                            width: "100%",
                                                            height: "100%",
                                                            borderRadius: "full",
                                                            bg: "red.500",
                                                            opacity: 0.5,
                                                            animation: 'pulse 1.5s infinite',
                                                        }}
                                                    />
                                                )}
                                            </Link>
                                        </Box>
                                    ))}
                                </HStack>
                            </Flex>
                        )}

                        {/* Right side - Play Now and Wallet */}
                        <Flex align="center" gap={4} pr={2}>
                            {!isMobile && (
                                <>
                                    {isGamePage && (
                                        <Flex 
                                            align="center" 
                                            bg="rgba(0, 230, 118, 0.1)" 
                                            px={4} 
                                            py={2} 
                                            borderRadius="full"
                                            border="1px solid"
                                            borderColor="rgba(0, 230, 118, 0.3)"
                                            mr={2}
                                        >
                                            <Icon as={FaCoins} color="#00E676" mr={2} />
                                            <Text color="#00E676" fontWeight="bold">
                                                {balance?.jinu?.toLocaleString() || '0'} JINU
                                            </Text>
                                        </Flex>
                                    )}
                                    {!isGamePage ? (
                                        <Button
                                            bg="transparent"
                                            border="2px solid"
                                            borderColor="#00E676"
                                            color="#00E676"
                                            _hover={{
                                                bg: '#00E676',
                                                color: 'black',
                                            }}
                                            size="md"
                                            rounded="full"
                                            px={6}
                                            h="40px"
                                            onClick={handleEnterRealm}
                                        >
                                            Enter Realm
                                        </Button>
                                    ) : (
                                        <Link to="/">
                                            <Button
                                                bg="transparent"
                                                border="2px solid"
                                                borderColor="#00E676"
                                                color="#00E676"
                                                _hover={{
                                                    bg: '#00E676',
                                                    color: 'black',
                                                }}
                                                size="md"
                                                rounded="full"
                                                px={6}
                                                h="40px"
                                            >
                                                Back to Home
                                            </Button>
                                        </Link>
                                    )}
                                </>
                            )}
                            {isMobile && (
                                <ChakraIconButton
                                    aria-label="Open menu"
                                    icon={<FaBars />}
                                    variant="ghost"
                                    color="white"
                                    onClick={onOpen}
                                    _hover={{ color: "#00e676" }}
                                />
                            )}
                        </Flex>
                    </Flex>
                </Container>
            </Box>

            {/* Mobile Menu Drawer */}
            <Drawer isOpen={isOpen} placement="right" onClose={onClose}>
                <DrawerOverlay />
                <DrawerContent bg="#0D1019">
                    <DrawerCloseButton color="white" />
                    <DrawerHeader borderBottomWidth="1px" borderColor="whiteAlpha.200">
                        <Flex align="center">
                            <Image
                                src="/images/palm-heist-logo.png"
                                alt="PALMHEIST Logo"
                                h="40px"
                                objectFit="contain"
                                mr={3}
                            />
                            <Text
                                fontSize="2xl"
                                fontWeight="bold"
                                color="white"
                                bgGradient="linear(to-r, #00E676, #00B894)"
                                bgClip="text"
                            >
                                PALMHEIST
                            </Text>
                        </Flex>
                    </DrawerHeader>
                    <DrawerBody>
                        <VStack spacing={6} align="stretch" mt={8}>
                            {navItems.map((link) => (
                                <Box key={link.path} position="relative">
                                    <Link
                                        to={link.path}
                                        onClick={(e) => handleNavClick(e, link.path)}
                                    >
                                        <Text
                                            color={link.isSpecial ? "#00e676" : "white"}
                                            _hover={{
                                                color: "#00e676",
                                            }}
                                            px={4}
                                            fontWeight="medium"
                                            transition="color 0.2s"
                                            fontSize="lg"
                                        >
                                            {link.label}
                                        </Text>
                                        {link.isSpecial && (
                                            <Box
                                                position="absolute"
                                                top="2px"
                                                right="6px"
                                                width="6px"
                                                height="6px"
                                                borderRadius="full"
                                                bg="red.500"
                                                sx={{
                                                    '@keyframes pulse': {
                                                        '0%': {
                                                            transform: 'scale(0.95)',
                                                            opacity: 0.5,
                                                        },
                                                        '50%': {
                                                            transform: 'scale(1.1)',
                                                            opacity: 0.8,
                                                        },
                                                        '100%': {
                                                            transform: 'scale(0.95)',
                                                            opacity: 0.5,
                                                        },
                                                    },
                                                    animation: 'pulse 1.5s infinite',
                                                }}
                                            />
                                        )}
                                    </Link>
                                </Box>
                            ))}
                            <Link to="/game" onClick={onClose}>
                                <Button
                                    w="full"
                                    bg="transparent"
                                    border="2px solid"
                                    borderColor="#00E676"
                                    color="#00E676"
                                    _hover={{
                                        bg: '#00E676',
                                        color: 'black',
                                    }}
                                    size="md"
                                    rounded="full"
                                    px={6}
                                    h="40px"
                                >
                                    Play Now
                                </Button>
                            </Link>
                        </VStack>
                    </DrawerBody>
                </DrawerContent>
            </Drawer>
            {/* Social Prompt Modal */}
            {showSocialPrompt && (
                <SocialPrompt 
                    onComplete={handleSocialPromptComplete} 
                />
            )}
            
            {/* Access Denied Prompt */}
            {showAccessDeniedPrompt && (
                <WhitelistAccessDenied 
                    onComplete={handleComingSoonComplete} 
                />
            )}
        </Box>
    );
};

export default NavBar;
